import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  LayoutChangeEvent,
  Pressable,
  Text,
  View,
} from 'react-native';
import { DateTime } from 'luxon';
import { ChevronLeftIcon, ChevronRightIcon } from '../../icons';
import { calendarStyles } from './calendar.styles';
import {
  monthLabel,
  monthMatrix,
  navigateAnchor,
  weekDays,
  weekLabel,
} from './calendar.dates';
import { MonthGrid } from './MonthGrid';
import { WeekGrid } from './WeekGrid';
import { NavButton } from './NavButton';
import { LoadingIndicator } from '../../../loadingIndicator';
import { CalendarDragContext } from './calendar.drag';
import { useCalendarDragController } from './useCalendarDragController';
import type { CalendarEvent, CalendarMode } from './calendar.types';

/** Below this container width the "Today" button is hidden (§1 / gripp-OS 400px). */
const TODAY_HIDE_BREAKPOINT = 400;

const MODES: CalendarMode[] = ['week', 'month'];

/**
 * The currently-visible date range of the calendar grid. For month mode this is
 * the full 6×7 matrix (leading Sunday → trailing Saturday), NOT just the anchor
 * month — so a query scoped to this range covers every cell the user can see.
 * For week mode it is Sunday→Saturday of the anchor week.
 */
export interface CalendarVisibleRange {
  /** First visible cell, at `startOf('day')` (inclusive). */
  start: DateTime;
  /** Last visible cell, at `endOf('day')` (inclusive). */
  end: DateTime;
}

export interface CalendarProps {
  /** Events to render across both views (mock data in P1; real in P2). */
  events: CalendarEvent[];
  /** Initial display mode. @default 'week' (matches gripp-OS). */
  initialMode?: CalendarMode;
  /** Initial anchor date. @default today. */
  initialAnchor?: DateTime;
  /** Currently-open activity id (highlighted pill). */
  selectedId?: string | null;
  /** Show the asset second line inside pills (§1 `showAssetInPill`). */
  showAsset?: boolean;
  /** Open an activity (P2 wires `/activity/:id`). */
  onOpenEvent?: (id: string) => void;
  /**
   * Fires on mount and whenever the visible range changes (anchor OR mode).
   * The Calendar owns anchor+mode internally, so this is how a parent learns
   * which date window to fetch (P2 `useActivityCalendar`). The range is the
   * full visible grid: month → leading Sunday `.startOf('day')` through trailing
   * Saturday `.endOf('day')`; week → Sunday `.startOf('day')` through Saturday
   * `.endOf('day')`.
   */
  onVisibleRangeChange?: (range: CalendarVisibleRange) => void;
  /** Month-cell press passthrough (P3 drop wiring). */
  onCellPress?: (day: DateTime, index: number) => void;
  /** Week-slot press passthrough (P3 drop wiring). */
  onSlotPress?: (day: DateTime, hour: number) => void;
  /**
   * Enable pointer/long-press drag-to-reschedule (§3). Default `false` — pills
   * are click-to-open only. P3.S2 passes `true` only when the caller may update
   * the issue (permission-gated affordance, per the frontend-gates rule).
   */
  draggable?: boolean;
  /**
   * Fires after a successful drag-drop with the event's computed new date
   * (month → same time-of-day, new day; week → dropped hour + day). The screen
   * owns persistence — P3.S2 wires this to `useUpdateIssue({ adjustCreationDate })`.
   * Nothing is mutated here; a no-op drop (same day+hour) does not fire.
   */
  onEventDrop?: (eventId: string, newDate: DateTime) => void;
  /**
   * Show the standard app loading spinner over the grid while the visible
   * window's data is being fetched. Only shown when there are no events yet, so
   * navigating to an already-loaded window doesn't flash a spinner. The window
   * query can take several seconds, so this signals "loading" vs. "empty".
   */
  loading?: boolean;
}

/**
 * Calendar container (plan 0017 §1 "Toolbar" + "Week/month mode toggle"). Composes
 * MonthGrid + WeekGrid behind a single toolbar: date label (left), prev/today/next
 * nav, and the week/month mode toggle (right). Drives anchor + mode state; data
 * comes in via `events` (no fetching here — that's P2). The "Today" text button
 * hides below a 400px *container* width, measured with onLayout to match gripp-OS's
 * container-query breakpoint (RN-Web has no `@container`).
 */
export const Calendar: React.FC<CalendarProps> = ({
  events,
  initialMode = 'week',
  initialAnchor,
  selectedId,
  showAsset = false,
  onOpenEvent,
  onCellPress,
  onSlotPress,
  onVisibleRangeChange,
  draggable = false,
  onEventDrop,
  loading = false,
}) => {
  const today = useMemo(() => DateTime.now(), []);
  const dragController = useCalendarDragController(draggable, onEventDrop);
  const [mode, setMode] = useState<CalendarMode>(initialMode);
  const [anchor, setAnchor] = useState<DateTime>(initialAnchor ?? DateTime.now());
  const [containerWidth, setContainerWidth] = useState(0);
  const [hoverToday, setHoverToday] = useState(false);

  const label = mode === 'month' ? monthLabel(anchor) : weekLabel(anchor);
  const showTodayButton = containerWidth === 0 || containerWidth >= TODAY_HIDE_BREAKPOINT;

  const onLayout = (e: LayoutChangeEvent) => {
    setContainerWidth(e.nativeEvent.layout.width);
  };

  const goPrev = () => setAnchor((a: DateTime) => navigateAnchor(a, mode, -1));
  const goNext = () => setAnchor((a: DateTime) => navigateAnchor(a, mode, 1));
  const goToday = () => setAnchor(DateTime.now());

  // Publish the visible range to the parent on mount and whenever anchor/mode
  // change (the parent uses it to scope the data fetch). Held in a ref so an
  // inline-callback parent doesn't re-fire this effect every render — only the
  // computed range (anchor + mode) drives it.
  const onVisibleRangeChangeRef = useRef(onVisibleRangeChange);
  onVisibleRangeChangeRef.current = onVisibleRangeChange;
  useEffect(() => {
    const cells = mode === 'month' ? monthMatrix(anchor) : weekDays(anchor);
    const start = cells[0].startOf('day');
    const end = cells[cells.length - 1].endOf('day');
    onVisibleRangeChangeRef.current?.({ start, end });
  }, [anchor, mode]);

  return (
    <View style={calendarStyles.container} onLayout={onLayout}>
      {/* Toolbar */}
      <View style={calendarStyles.toolbar}>
        <Text style={calendarStyles.toolbarLabel}>{label}</Text>
        <View style={calendarStyles.navGroup}>
          <NavButton
            icon={ChevronLeftIcon}
            accessibilityLabel="Previous"
            onPress={goPrev}
          />
          {showTodayButton ? (
            <Pressable
              onPress={goToday}
              onHoverIn={() => setHoverToday(true)}
              onHoverOut={() => setHoverToday(false)}
              style={[
                calendarStyles.todayButton,
                hoverToday && calendarStyles.todayButtonHovered,
              ]}
              accessibilityRole="button"
              accessibilityLabel="Today"
            >
              <Text style={calendarStyles.todayButtonText}>Today</Text>
            </Pressable>
          ) : null}
          <NavButton
            icon={ChevronRightIcon}
            accessibilityLabel="Next"
            onPress={goNext}
          />
        </View>

        <View style={calendarStyles.toolbarSpacer} />

        {/* Week/month mode toggle */}
        <View style={calendarStyles.modeToggle}>
          {MODES.map((m) => {
            const active = mode === m;
            return (
              <Pressable
                key={m}
                onPress={() => setMode(m)}
                style={[
                  calendarStyles.modeButton,
                  active && calendarStyles.modeButtonActive,
                ]}
                accessibilityRole="button"
                accessibilityState={{ selected: active }}
                accessibilityLabel={`${m} view`}
              >
                <Text
                  style={[
                    calendarStyles.modeButtonText,
                    active && calendarStyles.modeButtonTextActive,
                  ]}
                >
                  {m}
                </Text>
              </Pressable>
            );
          })}
        </View>
      </View>

      {/* Grid region — fills remaining height (week's ScrollView needs the bound).
          Wrapped in the drag context so pills/cells can register geometry and
          read the active drop target (the controller is a no-op when !draggable). */}
      <CalendarDragContext.Provider value={dragController}>
        <View style={calendarStyles.gridRegion}>
          {mode === 'month' ? (
            <MonthGrid
              anchor={anchor}
              today={today}
              events={events}
              selectedId={selectedId}
              showAsset={showAsset}
              onOpenEvent={onOpenEvent}
              onCellPress={onCellPress}
            />
          ) : (
            <WeekGrid
              anchor={anchor}
              today={today}
              events={events}
              selectedId={selectedId}
              showAsset={showAsset}
              onOpenEvent={onOpenEvent}
              onSlotPress={onSlotPress}
            />
          )}
          {/* Standard app spinner overlay while a window is fetching (the query
              can take seconds). Overlaid — not a replace — so it shows whether the
              grid is empty (new month) or still showing the previous month. */}
          {loading ? (
            <View style={calendarStyles.loadingOverlay} pointerEvents="auto">
              <LoadingIndicator backgroundColor="rgba(255,255,255,0.6)" />
            </View>
          ) : null}
        </View>
      </CalendarDragContext.Provider>
    </View>
  );
};

Calendar.displayName = 'Calendar';
